import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { assert, assertOneOf } from "@/utils/assertions";
import { sanitizeText } from "@/utils/sanitization";
import { RateLimitPresets, checkRateLimit } from "@/utils/rateLimiter";
import { useOperationLock } from "@/utils/stateManagement";
import { toast } from "sonner";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  Calendar,
  Clock,
  DollarSign,
  Download,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import Navbar from "@/components/layout/navbar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

// Mock data for demonstration
const mockUsageData = [
  { date: "2024-01-01", usage: 145, revenue: 234.50 },
  { date: "2024-01-02", usage: 189, revenue: 289.80 },
  { date: "2024-01-03", usage: 156, revenue: 256.20 },
  { date: "2024-01-04", usage: 203, revenue: 345.10 },
  { date: "2024-01-05", usage: 178, revenue: 301.60 },
  { date: "2024-01-06", usage: 192, revenue: 326.40 },
  { date: "2024-01-07", usage: 165, revenue: 280.50 },
];

const mockAgentPerformance = [
  { name: "Code Review Assistant", usage: 542, revenue: 892.30, successRate: 98.5 },
  { name: "Security Scanner Pro", usage: 423, revenue: 718.10, successRate: 99.2 },
  { name: "Test Automation Suite", usage: 312, revenue: 530.40, successRate: 97.8 },
  { name: "Performance Optimizer", usage: 189, revenue: 321.30, successRate: 96.5 },
];

const mockCategoryDistribution = [
  { name: "Development", value: 35, color: "#0088FE" },
  { name: "Testing", value: 25, color: "#00C49F" },
  { name: "Security", value: 20, color: "#FFBB28" },
  { name: "Data Analysis", value: 15, color: "#FF8042" },
  { name: "Other", value: 5, color: "#8884D8" },
];

const mockRevenueBreakdown = [
  { agent: "Code Review Assistant", amount: 892.30, users: 145, avgPerUser: 6.15 },
  { agent: "Security Scanner Pro", amount: 718.10, users: 98, avgPerUser: 7.33 },
  { agent: "Test Automation Suite", amount: 530.40, users: 76, avgPerUser: 6.98 },
  { agent: "Performance Optimizer", amount: 321.30, users: 52, avgPerUser: 6.18 },
  { agent: "Documentation Generator", amount: 245.60, users: 41, avgPerUser: 5.99 },
];

// Valid time ranges and metrics
const VALID_TIME_RANGES = ['24h', '7d', '30d', '90d', '1y'] as const;
type TimeRange = typeof VALID_TIME_RANGES[number];
type Metric = 'usage' | 'revenue';

export function DeveloperAnalyticsPortal() {
  const [timeRange, setTimeRange] = useState<TimeRange>("7d");
  const [selectedMetric, setSelectedMetric] = useState<Metric>("usage");
  const { acquireLock } = useOperationLock();
  
  // Track component lifecycle
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // TODO: Replace with actual GraphQL queries
  // const { data: analyticsData, loading } = useQuery(GET_DEVELOPER_ANALYTICS, {
  //   variables: { timeRange },
  // });

  // const loading = false;

  // Calculate summary statistics with validation
  const { totalUsage, totalRevenue } = useMemo(() => {
    let usage = 0;
    let revenue = 0;
    
    for (const day of mockUsageData) {
      // Invariants: values must be non-negative
      assert(day.usage >= 0, 'Usage must be non-negative');
      assert(day.revenue >= 0, 'Revenue must be non-negative');
      
      usage += day.usage;
      revenue += day.revenue;
    }
    
    return { totalUsage: usage, totalRevenue: revenue };
  }, []);

  // Calculate trends (comparing to previous period)
  const usageTrend = 12.5; // Positive trend
  const revenueTrend = 15.3; // Positive trend
  const usersTrend = 8.2; // Positive trend

  const exportData = useCallback(async () => {
    // Check rate limit
    const userId = 'current-user'; // TODO: Get from auth context
    if (!checkRateLimit(userId, 'export-analytics', RateLimitPresets.FILE_UPLOAD)) {
      toast.error('Too many export requests. Please wait.');
      return;
    }
    
    try {
      await acquireLock('export-analytics', async () => {
        // Pre-condition: Time range must be valid
        assertOneOf(timeRange, VALID_TIME_RANGES, 'Time range');
        
        // TODO: Implement data export functionality
        console.log('Exporting analytics data...', { timeRange });
        
        // Post-condition: Export should complete successfully
        if (isMountedRef.current) {
          toast.success('Analytics data exported successfully');
        }
      });
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export analytics data');
    }
  }, [timeRange, acquireLock]);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <div className="container mx-auto py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Developer Analytics Portal</h1>
          <p className="mt-2 text-muted-foreground">
            Track performance, usage, and revenue for your specialized agents
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select 
            value={timeRange} 
            onValueChange={(value) => {
              assertOneOf(value as TimeRange, VALID_TIME_RANGES, 'Time range');
              setTimeRange(value as TimeRange);
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportData}>
            <Download className="mr-2 h-4 w-4" />
            Export Data
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mb-8 grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalRevenue.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              {revenueTrend > 0 ? (
                <span className="flex items-center text-green-600">
                  <TrendingUp className="mr-1 h-3 w-3" />
                  +{revenueTrend}% from last period
                </span>
              ) : (
                <span className="flex items-center text-red-600">
                  <TrendingDown className="mr-1 h-3 w-3" />
                  {revenueTrend}% from last period
                </span>
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Usage</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsage.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {usageTrend > 0 ? (
                <span className="flex items-center text-green-600">
                  <TrendingUp className="mr-1 h-3 w-3" />
                  +{usageTrend}% from last period
                </span>
              ) : (
                <span className="flex items-center text-red-600">
                  <TrendingDown className="mr-1 h-3 w-3" />
                  {usageTrend}% from last period
                </span>
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">412</div>
            <p className="text-xs text-muted-foreground">
              {usersTrend > 0 ? (
                <span className="flex items-center text-green-600">
                  <TrendingUp className="mr-1 h-3 w-3" />
                  +{usersTrend}% from last period
                </span>
              ) : (
                <span className="flex items-center text-red-600">
                  <TrendingDown className="mr-1 h-3 w-3" />
                  {usersTrend}% from last period
                </span>
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">1.2s</div>
            <p className="text-xs text-muted-foreground">
              <span className="flex items-center text-green-600">
                <TrendingDown className="mr-1 h-3 w-3" />
                -5.2% improvement
              </span>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="agents">Agent Performance</TabsTrigger>
          <TabsTrigger value="revenue">Revenue Analysis</TabsTrigger>
          <TabsTrigger value="users">User Insights</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Usage & Revenue Trends</CardTitle>
              <CardDescription>
                Daily usage and revenue for the selected time period
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex gap-2">
                <Button
                  variant={selectedMetric === "usage" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedMetric("usage")}
                  aria-label="Show usage metrics"
                >
                  Usage
                </Button>
                <Button
                  variant={selectedMetric === "revenue" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedMetric("revenue")}
                  aria-label="Show revenue metrics"
                >
                  Revenue
                </Button>
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={mockUsageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) =>
                      new Date(date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    }
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={(date) =>
                      new Date(date).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })
                    }
                  />
                  <Legend />
                  {selectedMetric === "usage" ? (
                    <Line
                      type="monotone"
                      dataKey="usage"
                      stroke="#8884d8"
                      name="Usage Count"
                    />
                  ) : (
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#82ca9d"
                      name="Revenue ($)"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Usage by Category</CardTitle>
                <CardDescription>
                  Distribution of agent usage across categories
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={mockCategoryDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {mockCategoryDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Performing Agents</CardTitle>
                <CardDescription>By usage count this period</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {mockAgentPerformance.slice(0, 4).map((agent, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{index + 1}.</span>
                        <div>
                          <p className="text-sm font-medium">{sanitizeText(agent.name)}</p>
                          <p className="text-xs text-muted-foreground">
                            {agent.usage} uses • ${agent.revenue.toFixed(2)} revenue
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary">{agent.successRate}%</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Agent Performance Tab */}
        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Agent Performance Metrics</CardTitle>
              <CardDescription>
                Detailed performance data for each of your agents
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={mockAgentPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="usage" fill="#8884d8" name="Usage Count" />
                  <Bar dataKey="revenue" fill="#82ca9d" name="Revenue ($)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Performance Details</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent Name</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Success Rate</TableHead>
                    <TableHead>Avg Response Time</TableHead>
                    <TableHead>User Rating</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockAgentPerformance.map((agent, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{agent.name}</TableCell>
                      <TableCell>{agent.usage.toLocaleString()}</TableCell>
                      <TableCell>${agent.revenue.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            agent.successRate >= 99
                              ? "default"
                              : agent.successRate >= 95
                              ? "secondary"
                              : "destructive"
                          }
                        >
                          {agent.successRate}%
                        </Badge>
                      </TableCell>
                      <TableCell>1.{index + 1}s</TableCell>
                      <TableCell>4.{9 - index}/5.0</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revenue Analysis Tab */}
        <TabsContent value="revenue" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Revenue Breakdown</CardTitle>
              <CardDescription>
                Detailed revenue analysis by agent and user segment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Active Users</TableHead>
                    <TableHead>Avg per User</TableHead>
                    <TableHead>Growth</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockRevenueBreakdown.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.agent}</TableCell>
                      <TableCell>${item.amount.toFixed(2)}</TableCell>
                      <TableCell>{item.users}</TableCell>
                      <TableCell>${item.avgPerUser.toFixed(2)}</TableCell>
                      <TableCell>
                        <span className="flex items-center text-green-600">
                          <TrendingUp className="mr-1 h-3 w-3" />
                          +{(Math.random() * 20 + 5).toFixed(1)}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Revenue Share</CardTitle>
                <CardDescription>Your earnings after platform fees</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span>Gross Revenue</span>
                    <span className="font-bold">${totalRevenue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Platform Fee (30%)</span>
                    <span>-${(totalRevenue * 0.3).toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between">
                      <span className="font-medium">Net Earnings</span>
                      <span className="font-bold text-green-600">
                        ${(totalRevenue * 0.7).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <Button className="w-full" variant="outline">
                    <Calendar className="mr-2 h-4 w-4" />
                    Schedule Payout
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Payment History</CardTitle>
                <CardDescription>Recent payouts and transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">January Payout</p>
                      <p className="text-xs text-muted-foreground">
                        Processed on Jan 15, 2024
                      </p>
                    </div>
                    <span className="font-medium">$1,823.45</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">December Payout</p>
                      <p className="text-xs text-muted-foreground">
                        Processed on Dec 15, 2023
                      </p>
                    </div>
                    <span className="font-medium">$1,567.80</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">November Payout</p>
                      <p className="text-xs text-muted-foreground">
                        Processed on Nov 15, 2023
                      </p>
                    </div>
                    <span className="font-medium">$1,234.50</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* User Insights Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>User Engagement Metrics</CardTitle>
              <CardDescription>
                Understanding how users interact with your agents
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Average Session Duration</p>
                  <p className="text-2xl font-bold">4m 32s</p>
                  <p className="text-xs text-muted-foreground">+12% from last period</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Return User Rate</p>
                  <p className="text-2xl font-bold">68%</p>
                  <p className="text-xs text-muted-foreground">+5% from last period</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">User Satisfaction</p>
                  <p className="text-2xl font-bold">4.6/5.0</p>
                  <p className="text-xs text-muted-foreground">Based on 234 reviews</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>User Feedback</CardTitle>
              <CardDescription>Recent reviews and ratings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="rounded-lg border p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-medium">{sanitizeText('John Doe')}</span>
                    <Badge>5.0 ⭐</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    "The Code Review Assistant has been invaluable for our team. It
                    catches issues we might have missed and suggests improvements
                    that make our code more maintainable."
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Code Review Assistant • 2 days ago
                  </p>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-medium">{sanitizeText('Jane Smith')}</span>
                    <Badge>4.5 ⭐</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    "Great security scanning capabilities. Would love to see more
                    detailed explanations for some of the findings."
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Security Scanner Pro • 4 days ago
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}